import { Head, useForm, Link } from '@inertiajs/react'

interface Props {
  errors: Record<string, string[]>
}

export default function Register({ errors }: Props) {
  const { data, setData, post, processing } = useForm({
    pending_tenant: {
      proposed_org_name: '',
      proposed_slug: '',
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
      notes: '',
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/users')
  }

  const getError = (field: string) => errors[field]?.[0]

  return (
    <>
      <Head title="Request Access - Pinnacle" />
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
        <div className="max-w-xl w-full">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-text-primary">Pinnacle</h1>
            <p className="text-lg text-text-secondary mt-2">Request access for your organization</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <p className="text-sm text-text-muted mb-5">
              Pinnacle accounts are provisioned by our team. Tell us about your organization and we&rsquo;ll send your admin an invitation once approved.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="proposed_org_name" className="block text-sm font-medium text-text-primary mb-1">Organization name</label>
                <input
                  id="proposed_org_name" type="text" required
                  value={data.pending_tenant.proposed_org_name}
                  onChange={e => setData('pending_tenant', { ...data.pending_tenant, proposed_org_name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                />
                {getError('proposed_org_name') && <p className="mt-1 text-sm text-[#ED1C24]">{getError('proposed_org_name')}</p>}
              </div>

              <div>
                <label htmlFor="proposed_slug" className="block text-sm font-medium text-text-primary mb-1">Preferred URL slug (optional)</label>
                <input
                  id="proposed_slug" type="text"
                  value={data.pending_tenant.proposed_slug}
                  onChange={e => setData('pending_tenant', { ...data.pending_tenant, proposed_slug: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  placeholder="acme-co"
                />
                {getError('proposed_slug') && <p className="mt-1 text-sm text-[#ED1C24]">{getError('proposed_slug')}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact_first_name" className="block text-sm font-medium text-text-primary mb-1">Your first name</label>
                  <input
                    id="contact_first_name" type="text" required
                    value={data.pending_tenant.contact_first_name}
                    onChange={e => setData('pending_tenant', { ...data.pending_tenant, contact_first_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-stone/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  />
                  {getError('contact_first_name') && <p className="mt-1 text-sm text-[#ED1C24]">{getError('contact_first_name')}</p>}
                </div>
                <div>
                  <label htmlFor="contact_last_name" className="block text-sm font-medium text-text-primary mb-1">Your last name</label>
                  <input
                    id="contact_last_name" type="text" required
                    value={data.pending_tenant.contact_last_name}
                    onChange={e => setData('pending_tenant', { ...data.pending_tenant, contact_last_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-stone/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  />
                  {getError('contact_last_name') && <p className="mt-1 text-sm text-[#ED1C24]">{getError('contact_last_name')}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="contact_email" className="block text-sm font-medium text-text-primary mb-1">Work email</label>
                <input
                  id="contact_email" type="email" required
                  value={data.pending_tenant.contact_email}
                  onChange={e => setData('pending_tenant', { ...data.pending_tenant, contact_email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  placeholder="you@yourcompany.com"
                />
                {getError('contact_email') && <p className="mt-1 text-sm text-[#ED1C24]">{getError('contact_email')}</p>}
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-text-primary mb-1">Anything we should know? (optional)</label>
                <textarea
                  id="notes" rows={3}
                  value={data.pending_tenant.notes}
                  onChange={e => setData('pending_tenant', { ...data.pending_tenant, notes: e.target.value })}
                  className="w-full px-4 py-2.5 border border-stone/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  placeholder="Team size, current tools, who referred you, etc."
                />
              </div>

              <button
                type="submit" disabled={processing}
                className="w-full bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold text-base py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
              >
                {processing ? 'Submitting…' : 'Request access'}
              </button>
            </form>

            <div className="mt-6 text-center text-base text-text-secondary">
              Already have an account?{' '}
              <Link href="/users/sign_in" className="text-pinnacle-red hover:underline font-medium">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
